import { fetchDownloadFile } from "@/api/user/home";
import { CreditDataMain } from "@/types/api/user/home.type.data";
import { numberSeparator, toPersianNumber } from "@/hooks/useThousandsSeparator";
import BoxKit from "@/shared/components/kit/box";
import CustomButton from "@/shared/components/kit/button";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { Grid } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import React, { SetStateAction, useState } from "react";
import { PulseLoader } from "react-spinners";
import { toast } from "react-toastify";

// ==================== Types ====================
interface CreditDetailsProps {
  userInquiry?: CreditDataMain;
  setDetail?: React.Dispatch<SetStateAction<boolean>>;
}

interface ImageConfig {
  key: string;
  src?: string;
  title: string;
}

// ==================== Constants ====================
const GRADIENT_BACKGROUND = {
  background: "linear-gradient(97.77deg, rgba(255, 255, 255, 0.4) -9.73%, rgba(255, 255, 255, 0.281106) 16.04%, rgba(255, 255, 255, 0.25) 50.23%, rgba(255, 255, 255, 0.231942) 87.66%, rgba(255, 255, 255) 109.42%)",
} as const;

const CONNECTED_BOX_STYLES = {
  height: "61px",
  display: "flex",
  alignItems: "center",
  color: "#000",
  paddingRight: "10px",
  width: "25%",
  boxSizing: "border-box" as const,
  borderRight: "1px solid #383838",
  borderLeft: "none",
} as const;

const IMAGE_STYLES = {
  container: {
    width: { xs: "100%", sm: "45%", md: "20%" },
    height: "149px",
    ...GRADIENT_BACKGROUND,
    marginBottom: { xs: "1rem", sm: 0 },
  },
  imageWrapper: {
    width: "100%",
    height: "85px",
    overflow: "hidden",
    borderRadius: "8px",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
  },
} as const;

const LOADER_CONFIG = {
  color: "#fff",
  size: 5,
  speedMultiplier: 1,
} as const;

// ==================== Sub-Components ====================

const ConnectedRow: React.FC<{ items: string[] }> = ({ items }) => (
    <StackKit
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        sx={{
          border: "1px solid #136A74",
          overflow: "hidden",
          width: "100%",
        }}
    >
      {items.map((text, index) => (
          <BoxKit
              key={index}
              sx={{
                ...CONNECTED_BOX_STYLES,
                width: { xs: "100%", sm: `${100 / items.length}%` },
                borderRight: {
                  xs: "none",
                  sm: index < items.length - 1 ? "1px solid #fefefe" : "none",
                },
                borderBottom: {
                  xs: index < items.length - 1 ? "1px solid #fefefe" : "none",
                  sm: "none",
                },
              }}
          >
            {text}
          </BoxKit>
      ))}
    </StackKit>
);

const SingleFullRow: React.FC<{ content: string | React.ReactNode }> = ({ content }) => (
    <BoxKit
        sx={{
          minHeight: "61px",
          display: "flex",
          alignItems: "center",
          color: "#000",
          paddingRight: "10px",
          width: "100%",
          overflow: "hidden",
          flexWrap: "wrap",
        }}
        className={'border border-[#136A74]'}
    >
      {content}
    </BoxKit>
);

const ImgBox: React.FC<{ src?: string; title: string }> = ({ src, title }) => {
  if (!src) return null;

  return (
      <StackKit sx={IMAGE_STYLES.container}>
        <BoxKit sx={IMAGE_STYLES.imageWrapper}>
          <img src={src} alt={title} style={IMAGE_STYLES.image} />
        </BoxKit>
        <TypoGraphyKit variant="body1" sx={{ textAlign: "center", marginTop: "0.5rem" }}>
          {title}
        </TypoGraphyKit>
      </StackKit>
  );
};

const ImagesGrid: React.FC<{ images: ImageConfig[] }> = ({ images }) => {
  const validImages = images.filter(img => img.src);
  if (validImages.length === 0) return null;

  const CHUNK_SIZE = 3;
  const imageChunks = validImages.reduce<ImageConfig[][]>((acc, curr, index) => {
    const chunkIndex = Math.floor(index / CHUNK_SIZE);
    if (!acc[chunkIndex]) acc[chunkIndex] = [];
    acc[chunkIndex].push(curr);
    return acc;
  }, []);

  return (
      <StackKit direction="column" gap={2} sx={{ width: "100%" }}>
        {imageChunks.map((chunk, index) => (
            <StackKit
                key={index}
                direction={{ xs: "column", sm: "row" }}
                justifyContent={{ xs: "center", sm: "space-around" }}
                alignItems="center"
                sx={{
                  width: "100%",
                  flexWrap: "wrap",
                  gap: 2,
                }}
            >
              {chunk.map(({ key, src, title }) => (
                  <ImgBox key={key} src={src} title={title} />
              ))}
            </StackKit>
        ))}
      </StackKit>
  );
};

// ==================== Helpers ====================

const downloadFile = (blob: Blob, fileName: string): void => {
  const fileURL = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = fileURL;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(fileURL);
};

const handleDownloadResponse = (response: unknown): void => {
  try {
    if (response instanceof Blob) {
      if (response.type === 'application/pdf' || response.type.includes('pdf')) {
        downloadFile(response, 'validation_result.pdf');
        toast.success("فایل PDF با موفقیت دانلود شد");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          const jsonData = JSON.parse(content);
          const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
            type: 'text/plain; charset=utf-8'
          });
          downloadFile(blob, 'validation_result.json');
          toast.success("فایل با موفقیت دانلود شد");
        } catch {
          downloadFile(response, 'validation_result.txt');
          toast.success("فایل با موفقیت دانلود شد");
        }
      };
      reader.readAsText(response);
      return;
    }

    if (typeof response === 'string') {
      try {
        const jsonData = JSON.parse(response);
        const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
          type: 'application/json; charset=utf-8'
        });
        downloadFile(blob, 'validation_result.json');
      } catch {
        const blob = new Blob([response], { type: 'text/plain; charset=utf-8' });
        downloadFile(blob, 'validation_result.txt');
      }
      toast.success("فایل با موفقیت دانلود شد");
      return;
    }

    const blob = new Blob([JSON.stringify(response, null, 2)], {
      type: 'application/json; charset=utf-8'
    });
    downloadFile(blob, 'validation_result.json');
    toast.success("فایل با موفقیت دانلود شد");
  } catch (error) {
    toast.error("خطا در دانلود فایل");
    console.error("Download error:", error);
  }
};

// ==================== Main Component ====================

/**
 * CreditDetails Component
 * Displays detailed credit information including personal info,
 * business details, and uploaded images.
 */
const CreditDetails: React.FC<CreditDetailsProps> = ({
                                                       userInquiry,
                                                       setDetail,
                                                     }) => {
  const [isPendingDownload, setIsPendingDownload] = useState(false);

  // ==================== Mutations ====================

  const { mutate } = useMutation({
    mutationFn: fetchDownloadFile,
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(error?.response?.data?.message || "خطا در دانلود فایل");
      setIsPendingDownload(false);
    },
    onSuccess: (response) => {
      handleDownloadResponse(response);
      setIsPendingDownload(false);
    },
  });

  // ==================== Handlers ====================

  const handleClickDownload = (): void => {
    setIsPendingDownload(true);
    mutate(undefined);
  };

  const handleClickReturn = (): void => {
    setDetail?.(false);
  };

  // ==================== Data Preparation ====================

  const imageConfigs: ImageConfig[] = [
    { key: 'nid_front', src: userInquiry?.nid_front_image, title: 'روی کارت ملی' },
    { key: 'nid_back', src: userInquiry?.nid_back_image, title: 'پشت کارت ملی' },
    { key: 'birth_certificate', src: userInquiry?.birth_certificate_image, title: 'صفحه اول شناسنامه' },
    { key: 'checkbook', src: userInquiry?.checkbook_image, title: 'تصویر چک ضمانت' },
    { key: 'job', src: userInquiry?.job_image, title: 'فیش حقوقی' },
    { key: 'lease', src: userInquiry?.lease_image, title: 'تصویر جواز کسب' },
  ];

  const personalInfo = [
    `نام: ${userInquiry?.name || "-"}`,
    `نام خانوادگی: ${userInquiry?.last_name || "-"}`,
    `شماره تلفن: ${toPersianNumber(userInquiry?.phone?.toString() || "-")}`,
    `کد ملی: ${toPersianNumber(userInquiry?.nid?.toString() || "-")}`,
  ];

  const businessInfo = [
    `شغل: ${userInquiry?.business?.business_type || "ثبت نشده"}`,
    `نام شرکت: ${userInquiry?.business?.name || "ثبت نشده"}`,
    `سمت شغلی: ${userInquiry?.business?.title || "ثبت نشده"}`,
    `شناسه ملی: ${userInquiry?.business?.company_nid || "ثبت نشده"}`,
  ];

  // ==================== Render ====================

  return (
      <Grid
          sx={{
            width: "100%",
            padding: { xs: "1rem", sm: "1.5rem", md: "2rem" },
            overflowX: "hidden",
          }}
      >
        <StackKit gap={2}>
          {/* Header Section */}
          <Grid
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
              }}
          >
            <TypoGraphyKit variant="body1">جزئیات اعتبار سنجی:</TypoGraphyKit>
            <StackKit direction="row" gap={2}>
              <CustomButton onClick={handleClickReturn}>بازگشت</CustomButton>
              <CustomButton onClick={handleClickDownload}>
                {isPendingDownload ? (
                    <StackKit sx={{ padding: "5px" }}>
                      <PulseLoader
                          color={LOADER_CONFIG.color}
                          size={LOADER_CONFIG.size}
                          speedMultiplier={LOADER_CONFIG.speedMultiplier}
                      />
                    </StackKit>
                ) : (
                    "نمایش فایل"
                )}
              </CustomButton>
            </StackKit>
          </Grid>

          {/* Main Information Section */}
          <StackKit
              className="border border-none justify-start w-full overflow-auto"
              sx={GRADIENT_BACKGROUND}
          >
            <ConnectedRow
                items={[
                  `مبلغ اعتبار درخواستی ${numberSeparator(userInquiry?.price)} ریال`,
                  `مدت بازپرداخت ${toPersianNumber(userInquiry?.month?.toString() || "0")} ماهه`,
                ]}
            />
            <ConnectedRow items={personalInfo} />
            <SingleFullRow
                content={`آدرس محل سکونت: ${userInquiry?.business?.address || "ثبت نشده"}`}
            />
            <ConnectedRow items={businessInfo} />
            <SingleFullRow
                content={
                  <StackKit sx={{ padding: "20px 0", flexDirection: "column", gap: 1 }}>
                    <TypoGraphyKit textAlign="right" variant="body1">
                      آدرس شرکت: {userInquiry?.address || "ثبت نشده"}
                    </TypoGraphyKit>
                  </StackKit>
                }
            />
          </StackKit>

          {/* Images Section */}
          <StackKit alignItems="start" sx={{ width: "100%" }}>
            <ImagesGrid images={imageConfigs} />
          </StackKit>
        </StackKit>
      </Grid>
  );
};

export default CreditDetails;